import { PkListStatItem } from '@uefadigital/panenka-react';
import { memo, useMemo } from 'react';

import { useWindowSize } from '@hooks/useWindowSize';
import PlayerIdentifier from '@shared/Identifier/PlayerIdentifier';
import { useTranslateFn } from '@shared/Translate';
import type { Statistic, StatisticDefinition } from '@shared-types/stats.types';
import { getStatNumericValue } from '@utilities/stats';

import * as styles from './styles.module.scss';

export const CoupleComparison = ({
  stats,
  statDefs,
  seasons,
  isStory,
}: {
  stats: Statistic[];
  statDefs: StatisticDefinition[];
  seasons: number[];
  isStory?: boolean;
}) => {
  const { isSmallMobile } = useWindowSize();
  const firstStats = useMemo(() => stats?.[0], [stats]);
  const secondStats = useMemo(() => stats?.[1], [stats]);
  const t = useTranslateFn();

  const showSeason = useMemo(
    () => firstStats?.playerId === secondStats?.playerId,
    [firstStats?.playerId, secondStats?.playerId],
  );

  const getSeasonLabel = (index: number) =>
    showSeason && `${t('season')} ${seasons[index] - 1}/${seasons[index] - Math.floor(seasons[index] / 100) * 100}`;

  const getLinkActive = (index: number) => {
    const season = seasons?.[index];
    return !!season && season >= window.currentSeason;
  };

  if ((!firstStats && !secondStats) || !statDefs?.length) return null;

  return (
    <div pk-theme={isStory ? 'dark' : 'light'} className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>{t('players_to_watch')}</div>
        <div className={styles.identifier}>
          <PlayerIdentifier
            className='pk-py--xs'
            player={firstStats.player}
            team={firstStats.team}
            useFullName={!isSmallMobile}
            showTeamName={!isSmallMobile}
            customSecondaryText={getSeasonLabel(0)}
            enableLink={getLinkActive(0)}
          />
        </div>
        <div className={styles.identifier}>
          <PlayerIdentifier
            player={secondStats.player}
            team={secondStats.team}
            useFullName={!isSmallMobile}
            showTeamName={!isSmallMobile}
            customSecondaryText={getSeasonLabel(1)}
            enableLink={getLinkActive(1)}
            badgeSlot='suffix'
          />
        </div>
      </div>
      {statDefs.map((type) => {
        const firstData = getStatNumericValue(firstStats, type);
        const secondData = getStatNumericValue(secondStats, type);
        if (firstData === null && secondData === null) return null;
        return (
          <PkListStatItem
            key={`stat_${type.name}`}
            className={styles.statItem}
            label={type.displayName}
            data={firstData}
            secondData={secondData}
            type='comparison'
          />
        );
      })}
    </div>
  );
};

export default memo(CoupleComparison);
